<template>
  <el-dialog
    title="选择考试题目"
    :visible.sync="dialogVisible"
    width="800px"
    @close="handleClose"
    append-to-body
  >
    <div class="exam-selector">
      <!-- 搜索栏 -->
      <div class="search-bar">
        <el-input
          v-model="searchForm.title"
          size="small"
          placeholder="搜索考试标题"
          style="width: 300px"
          @keyup.enter="handleSearch"
        >
          <el-button slot="append" icon="el-icon-search" @click="handleSearch"></el-button>
        </el-input>

        <el-cascader
          v-model="searchForm.category"
          :options="trainingCategoryTree"
          :props="categoryFilterProps"
          size="small"
          placeholder="选择分类"
          clearable
          filterable
          style="width: 220px; margin-left: 10px"
          @change="handleSearch"
        />
      </div>

      <!-- 考试列表 -->
      <el-table
        :data="examList"
        v-loading="loading"
        size="small"
        @selection-change="handleSelectionChange"
        style="width: 100%; margin-top: 16px"
      >
        <el-table-column type="selection" width="55" />

        <el-table-column prop="title" label="考试标题" min-width="200">
          <template slot-scope="scope">
            <div class="title-cell">
              <span class="title-text">{{ scope.row.title }}</span>
              <el-tag
                :type="scope.row.enabled ? 'success' : 'danger'"
                size="mini"
                style="margin-left: 8px"
              >
                {{ scope.row.enabled ? "启用" : "禁用" }}
              </el-tag>
            </div>
          </template>
        </el-table-column>

        <el-table-column prop="category" label="分类" min-width="160">
          <template slot-scope="scope">
            <el-tag size="small">{{ trainingCategoryText(scope.row.category) || "未分类" }}</el-tag>
          </template>
        </el-table-column>

        <el-table-column prop="questions" label="题目数量" width="100">
          <template slot-scope="scope">
            <el-tag size="mini" type="info">
              {{ scope.row.questions ? scope.row.questions.length : 0 }}题
            </el-tag>
          </template>
        </el-table-column>

        <el-table-column prop="totalScore" label="总分" width="80">
          <template slot-scope="scope">
            <span class="score-text">{{ scope.row.totalScore || 0 }}分</span>
          </template>
        </el-table-column>

        <el-table-column prop="passScore" label="及格分" width="80">
          <template slot-scope="scope">
            <span class="score-text">{{ scope.row.passScore || 0 }}分</span>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页 -->
      <div class="pagination-container">
        <el-pagination
          small
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
          :current-page="pagination.currentPage"
          :page-sizes="[10, 20, 50]"
          :page-size="pagination.pageSize"
          :total="pagination.total"
          layout="total, sizes, prev, pager, next, jumper"
        />
      </div>
    </div>

    <div slot="footer" class="dialog-footer">
      <el-button size="small" @click="handleClose">取消</el-button>
      <el-button size="small" type="primary" @click="handleConfirm" :disabled="selectedExams.length === 0">
        确定
      </el-button>
    </div>
  </el-dialog>
</template>

<script>
import { getTrainingExamList } from "@/api/new_training";
import { ParamUtil } from "@/util";
import trainingCategoryMixin from "../utils/trainingCategory";
export default {
  name: "ExamSelectorDialog",
  mixins: [trainingCategoryMixin],
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    category: {
      type: String,
      default: ""
    }
  },
  data() {
    return {
      dialogVisible: false,
      loading: false,
      examList: [],
      selectedExams: [],

      // 搜索表单
      searchForm: {
        title: "",
        category: ""
      },

      // 分页
      pagination: {
        currentPage: 1,
        pageSize: 10,
        total: 0
      }
    };
  },
  watch: {
    visible: {
      handler(val) {
        this.dialogVisible = val;
        if (val) {
          this.searchForm = {
            title: "",
            category: this.category || ""
          };
          this.pagination.currentPage = 1;
          this.selectedExams = [];
          this.loadExamList();
        }
      },
      immediate: true
    }
  },
  methods: {
    // 加载考试列表
    async loadExamList() {
      this.loading = true;
      try {
        const params = {
          page: {
            current: this.pagination.currentPage,
            size: this.pagination.pageSize
          },
          param: ParamUtil.filterEmptyData(this.searchForm)
        };
        const res = await getTrainingExamList(params);
        const { records = [], current = 1, total = 0 } = res.data || {};
        this.examList = records;
        this.pagination.currentPage = current;
        this.pagination.total = total;
      } catch (error) {
        this.$message.error("加载考试列表失败");
        console.error(error);
      } finally {
        this.loading = false;
      }
    },

    // 搜索
    handleSearch() {
      this.pagination.currentPage = 1;
      this.loadExamList();
    },

    // 选择变化
    handleSelectionChange(selection) {
      this.selectedExams = selection;
    },

    // 分页
    handleSizeChange(size) {
      this.pagination.pageSize = size;
      this.loadExamList();
    },

    handleCurrentChange(page) {
      this.pagination.currentPage = page;
      this.loadExamList();
    },

    // 确认选择
    handleConfirm() {
      if (this.selectedExams.length === 0) {
        this.$message.warning("请选择考试题目");
        return;
      }

      if (this.selectedExams.length > 1) {
        this.$message.warning("只能选择一个考试");
        return;
      }

      this.$emit("confirm", this.selectedExams[0]);
      this.handleClose();
    },

    // 关闭
    handleClose() {
      this.dialogVisible = false;
      this.$emit("update:visible", false);
    }
  }
};
</script>

<style lang="less" scoped>
.exam-selector {
  .search-bar {
    display: flex;
    align-items: center;
  }

  .title-cell {
    display: flex;
    align-items: center;

    .title-text {
      font-weight: 500;
    }
  }

  .score-text {
    font-weight: 600;
    color: #409eff;
  }

  .pagination-container {
    margin-top: 16px;
    text-align: right;
  }
}

.dialog-footer {
  text-align: right;
}
</style>
